import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'

import {
  CONTENT_STRATEGY_REFUSAL_MESSAGES,
  contentStrategyFailureMessageKey,
} from '@/components/setup/content-strategy/content-strategy-run'
import { i18n } from '@/i18n'
import { type CallableError, parseCallableError } from '@/lib/firebase/errors'
import {
  CONTENT_STRATEGY_NAVIGATOR,
  type ContentStrategyMove,
  resumeContentStrategyScreen,
} from '@/lib/setup/content-strategy-navigation'
import { screenIndex } from '@/lib/setup/setup-screen'
import { CONFIRM_STAMP_WAIT_MS, whenChanged } from '@/lib/setup/when-changed'
import {
  subscribeBeliefMap as repoSubscribeBeliefMap,
  subscribeContentStrategy as repoSubscribeContentStrategy,
} from '@/repositories/brand'
import {
  confirmContentStrategy as repoConfirmContentStrategy,
  isContentStrategyRefusalReason,
  isContentStrategyRunInProgressReason,
  startContentStrategy as repoStartContentStrategy,
  subscribeLatestContentStrategyRun as repoSubscribeLatestContentStrategyRun,
} from '@/repositories/content-strategy-run'
import { isOnboardingLaterStepRunningReason } from '@/repositories/setup'
import { useBrandStore } from '@/stores/brand'
import { useCustomerProfileSetupStore } from '@/stores/setup-customer-profile'
import { useSetupPositionStore } from '@/stores/setup-position'
import {
  currentSetupScope,
  useLatestRun,
  useScopedSubscription,
} from '@/stores/setup-subscriptions'
import { useToastStore } from '@/stores/toast'
import type { Belief } from '@/types/brand'
import {
  CONTENT_STRATEGY_RUN_STALE_AFTER_MS,
  type ContentStrategyRefusalReason,
  type ContentStrategyRun,
} from '@/types/content-strategy-run'
import { isError, isLoaded } from '@/types/load-state'
import { type StepConfirmation, isCurrentSince } from '@/types/onboarding-chain'
import { isRunFailed, isRunSettled, runActivity } from '@/types/setup-run'

export type StartContentStrategyOutcome =
  | { readonly status: 'started'; readonly runId: string }
  | { readonly status: 'refused'; readonly reason: ContentStrategyRefusalReason | undefined }

export interface ObjectiveEdit {
  readonly claim: string
  readonly objection: string
}

export interface CoreMessageEdit {
  readonly sentence: string
  readonly replaces: string
}

/** The toast line for a refused content-strategy call: plain words for a known reason, else the server's message. */
function describeRefusal(failure: CallableError): string {
  return isContentStrategyRefusalReason(failure.reason)
    ? i18n.global.t(CONTENT_STRATEGY_REFUSAL_MESSAGES[failure.reason])
    : failure.message
}

function refusalReason(failure: CallableError): ContentStrategyRefusalReason | undefined {
  return isContentStrategyRefusalReason(failure.reason) ? failure.reason : undefined
}

export const useContentStrategySetupStore = defineStore('setup-content-strategy', () => {
  const runSubscription = useLatestRun<ContentStrategyRun>(
    repoSubscribeLatestContentStrategyRun,
    CONTENT_STRATEGY_RUN_STALE_AFTER_MS,
  )
  const beliefMapSubscription = useScopedSubscription(repoSubscribeBeliefMap)
  const contentStrategySubscription = useScopedSubscription(repoSubscribeContentStrategy)

  const latestRun = runSubscription.state
  const run = runSubscription.run
  const beliefMapState = beliefMapSubscription.state
  const contentStrategyState = contentStrategySubscription.state

  const calling = ref(false)
  const awaitedRunId = ref<string | null>(null)
  const startFailed = ref(false)
  const confirming = ref(false)
  let autoStartAsked = false

  function open(): void {
    const scope = currentSetupScope()
    if (!scope) {
      const message = i18n.global.t('common.noWorkspaceSelected')
      runSubscription.fail(message)
      beliefMapSubscription.fail(message)
      contentStrategySubscription.fail(message)
      return
    }
    runSubscription.open(scope)
    beliefMapSubscription.open(scope)
    contentStrategySubscription.open(scope)
  }

  function close(): void {
    runSubscription.close()
    beliefMapSubscription.close()
    contentStrategySubscription.close()
    awaitedRunId.value = null
    startFailed.value = false
    autoStartAsked = false
  }

  const resolved = computed(
    () =>
      latestRun.value.status !== 'loading' &&
      beliefMapState.value.status !== 'loading' &&
      contentStrategyState.value.status !== 'loading',
  )

  const readFailed = computed(
    () =>
      isError(latestRun.value) ||
      isError(beliefMapState.value) ||
      isError(contentStrategyState.value),
  )

  const beliefMap = computed(() =>
    isLoaded(beliefMapState.value) ? beliefMapState.value.value : null,
  )

  const contentStrategy = computed(() =>
    isLoaded(contentStrategyState.value) ? contentStrategyState.value.value : null,
  )

  const coreMessage = computed(() => beliefMap.value?.obviousChoiceBelief ?? '')

  const beliefs = computed<readonly Belief[]>(() => beliefMap.value?.beliefs ?? [])

  const finished = computed(
    () =>
      beliefMap.value?.meta.status === 'approved' &&
      contentStrategy.value?.meta.status === 'approved',
  )

  const confirmation = computed<StepConfirmation>(() => ({
    confirmed: finished.value,
    confirmedAt: beliefMap.value?.meta.confirmedAt ?? null,
  }))

  const profileConfirmedAt = computed(
    () => useCustomerProfileSetupStore().profile?.meta.confirmedAt ?? null,
  )

  const draftCurrent = computed(
    () => run.value !== null && isCurrentSince(run.value.createdAt, profileConfirmedAt.value),
  )

  const currentRun = computed(() => (draftCurrent.value ? run.value : null))

  /** The core message the current draft wrote, or `null` until the draft has ended and its Belief Map arrived. */
  const draftedCoreMessage = computed(() => {
    const latest = currentRun.value
    const map = beliefMap.value
    if (latest === null || map === null || !isRunSettled(latest)) return null
    return isCurrentSince(map.meta.updatedAt, latest.createdAt) ? map.obviousChoiceBelief : null
  })

  watch(run, (latest) => {
    if (latest !== null && latest.id === awaitedRunId.value) awaitedRunId.value = null
  })

  const starting = computed(() => calling.value || awaitedRunId.value !== null)

  const isWriting = runSubscription.isLive

  const activity = computed(() => runActivity(run.value))

  const draftFailed = computed(
    () =>
      !starting.value &&
      (startFailed.value || (currentRun.value !== null && isRunFailed(currentRun.value))),
  )

  const failureMessageKey = computed(() =>
    draftFailed.value
      ? contentStrategyFailureMessageKey(currentRun.value, startFailed.value)
      : undefined,
  )

  function resumeIndex(): number {
    return screenIndex(
      CONTENT_STRATEGY_NAVIGATOR.screens,
      resumeContentStrategyScreen({
        hasBeliefMap: beliefMap.value !== null,
        hasStrategy: contentStrategy.value !== null,
        runLive: isWriting.value,
        current: draftCurrent.value,
        runFailed: currentRun.value !== null && isRunFailed(currentRun.value),
      }),
    )
  }

  function move(applied: ContentStrategyMove): void {
    useSetupPositionStore().move('strategy', CONTENT_STRATEGY_NAVIGATOR, applied)
  }

  /** Opens a draft and reports the run to watch or the reason it was refused; `keepEdited` leaves founder edits alone. */
  async function start(keepEdited: boolean): Promise<StartContentStrategyOutcome> {
    calling.value = true
    startFailed.value = false
    try {
      const { runId } = await repoStartContentStrategy({ keepEdited })
      if (run.value?.id !== runId) awaitedRunId.value = runId
      return { status: 'started', runId }
    } catch (cause) {
      const parsed = parseCallableError(cause)
      if (!isContentStrategyRunInProgressReason(parsed.reason)) {
        startFailed.value = true
        useToastStore().error(i18n.global.t('setup.errors.startFailed'), describeRefusal(parsed))
      }
      return { status: 'refused', reason: refusalReason(parsed) }
    } finally {
      calling.value = false
    }
  }

  /** Writes the strategy again, keeping founder edits. */
  async function writeAgain(): Promise<StartContentStrategyOutcome> {
    const outcome = await start(true)
    if (outcome.status === 'started') move('draft-opened')
    return outcome
  }

  async function retry(): Promise<StartContentStrategyOutcome> {
    return start(currentRun.value?.keepEdited ?? false)
  }

  watch(profileConfirmedAt, () => {
    autoStartAsked = false
  })

  watch(
    () =>
      useSetupPositionStore().position?.step === 'strategy' &&
      resolved.value &&
      !readFailed.value &&
      !starting.value &&
      !isWriting.value &&
      !draftCurrent.value,
    (due) => {
      if (!due || autoStartAsked) return
      autoStartAsked = true
      void start(false)
    },
    { immediate: true },
  )

  /** Saves the claim and objection of one objective, marking it edited. */
  async function saveObjective(beliefSlug: string, edit: ObjectiveEdit): Promise<boolean> {
    const belief = beliefs.value.find((candidate) => candidate.slug === beliefSlug)
    if (!belief) return false
    return useBrandStore().saveBeliefMapEdit(
      {
        section: 'belief',
        mode: 'replace',
        beliefSlug,
        belief: {
          phase: belief.phase,
          name: belief.name,
          claim: edit.claim,
          objection: edit.objection,
        },
      },
      describeRefusal,
    )
  }

  /** Saves the core message and the view it replaces, marking it edited; an empty message is refused without a call. */
  async function saveCoreMessage(edit: CoreMessageEdit): Promise<boolean> {
    const sentence = edit.sentence.trim()
    if (sentence === '') {
      useToastStore().error(
        i18n.global.t('brand.toast.saveFailed'),
        i18n.global.t(CONTENT_STRATEGY_REFUSAL_MESSAGES.obvious_choice_belief_missing),
      )
      return false
    }
    return useBrandStore().saveBeliefMapEdit(
      {
        section: 'roof',
        obviousChoiceBelief: sentence,
        obviousChoiceBeliefReplaces: edit.replaces.trim(),
      },
      describeRefusal,
    )
  }

  async function confirm(): Promise<boolean> {
    if (confirming.value) return false
    confirming.value = true
    try {
      const before = confirmation.value.confirmedAt
      await repoConfirmContentStrategy()
      await whenChanged(() => confirmation.value.confirmedAt, before, CONFIRM_STAMP_WAIT_MS)
      return true
    } catch (cause) {
      const parsed = parseCallableError(cause)
      if (isOnboardingLaterStepRunningReason(parsed.reason)) {
        useToastStore().error(i18n.global.t('setup.locked.laterStepRunning'))
      } else {
        useToastStore().error(i18n.global.t('setup.errors.confirmFailed'), describeRefusal(parsed))
      }
      return false
    } finally {
      confirming.value = false
    }
  }

  return {
    latestRun,
    run,
    currentRun,
    draftCurrent,
    beliefMapState,
    contentStrategyState,
    beliefMap,
    contentStrategy,
    coreMessage,
    draftedCoreMessage,
    beliefs,
    isWriting,
    starting,
    startFailed,
    draftFailed,
    failureMessageKey,
    confirming,
    resolved,
    readFailed,
    finished,
    confirmation,
    activity,
    open,
    close,
    resumeIndex,
    move,
    start,
    writeAgain,
    retry,
    saveObjective,
    saveCoreMessage,
    confirm,
  }
})
